import * as Sentry from '@sentry/react'
import { isSecretLinkPage, redactSecretLinks } from '@/utils/secretLinkPage'

const DSN = 'https://e93f7e70b1db427f8ee19c336644275a@glitchtip.coderoff.us/1'
const PROD_HOSTS = ['agent.numeo.ai', 'app.numeo.ai']

/**
 * The event with every signing link's token taken out. The page address, the breadcrumbs for each request and the
 * navigation history all carry it, so the whole event is rewritten rather than a list of known fields.
 */
const withoutSecretLinks = (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
    try {
        return JSON.parse(redactSecretLinks(JSON.stringify(event)))
    } catch {
        // An event that can't be rewritten is sent as it is, except from a page whose address is the secret.
        return isSecretLinkPage() ? null : event
    }
}

const enabled = typeof window !== 'undefined' && PROD_HOSTS.includes(window.location.hostname) && process.env.NODE_ENV !== 'test'

export const initErrorTracking = (): void => {
    if (!enabled) return
    try {
        Sentry.init({
            dsn: DSN,
            environment: 'production',
            release: process.env.REACT_APP_RELEASE_DATE,
            tracesSampleRate: 0,
            autoSessionTracking: false,
            integrations: [Sentry.breadcrumbsIntegration({ console: true, dom: true, fetch: true, history: true, xhr: true })],
            beforeSend: (event) => {
                if (event.exception?.values?.some((ex) => ex.value?.includes("Unexpected token '<'"))) return null
                return withoutSecretLinks(event)
            },
        })
        Sentry.setTag('service', 'numeo-web-app')
    } catch (err) {
        console.error('[errorTracking] init failed', err)
    }
}

interface ErrorTrackingUser {
    id?: string
    email?: string
    companyName?: string
}

export const setErrorTrackingUser = (user: ErrorTrackingUser | null): void => {
    if (!enabled) return
    try {
        if (!user) {
            Sentry.setUser(null)
            return
        }
        Sentry.setUser({ id: user.id, email: user.email })
        if (user.companyName) Sentry.setTag('company', user.companyName)
    } catch (err) {
        console.error('[errorTracking] setUser failed', err)
    }
}

/**
 * Derive a human-readable feature name from the current URL path.
 *
 * GlitchTip groups admin-panel issues by this `feature` tag, so the buckets here
 * are the source of truth for the "BY FEATURE" split in the reporter — keep them
 * aligned with the real route tree (see pages/dashboard/routes.tsx and its children).
 *
 * Most areas classify on the first path segment; a few `/dispatcher/*` pages
 * (ai-hub, load-radar, load-hub, faq) break out into their own product buckets.
 */
export const deriveFeatureFromPath = (pathname: string): string => {
    const path = pathname.toLowerCase()
    const [, seg1 = '', seg2 = ''] = path.split('/')

    switch (seg1) {
        case 'dispatcher':
            if (seg2 === 'ai-hub') return 'AI Hub'
            if (seg2 === 'load-radar') return 'Load Radar'
            if (seg2 === 'load-hub') return 'LoadHub'
            if (seg2 === 'faq') return 'FAQ'
            if (seg2 === 'settings') return 'General Settings' // route redirects to /settings, but tag the origin too
            return 'Dispatcher'
        case 'spot':
            return 'Dispatcher'
        case 'updater-agent':
            return 'Updater'
        case 'accounting':
            return 'Accounting'
        case 'billing':
            return 'Billing'
        case 'profiles':
        case 'truck-availability-report':
            return 'Profiles'
        case 'fleet':
            return 'Fleet'
        case 'safety':
            return 'Safety'
        case 'emails':
            return 'Email'
        case 'fuel-ai':
            return 'Fuel AI'
        case 'call-flow':
            return 'Call Flow'
        case 'identity-protection':
            return 'Identity Protection'
        case 'faq':
            return 'FAQ'
        case 'settings':
            return 'General Settings'
        case 'auth':
            return 'Auth'
        case 'onboarding':
        case 'mobile-onboarding':
        case 'ext':
        case 'ext-v2':
            return 'Onboarding'
        default:
            return 'Other'
    }
}

/**
 * Tag all subsequent GlitchTip events with the feature derived from the current
 * path. Set as a global tag (not a scoped one) so it sticks to every capture
 * path — error boundary, axios 5xx interceptor, and any direct captureException.
 * Call on every route change.
 */
export const setErrorTrackingFeature = (pathname: string): void => {
    if (!enabled) return
    try {
        Sentry.setTag('feature', deriveFeatureFromPath(pathname))
    } catch (err) {
        console.error('[errorTracking] setFeature failed', err)
    }
}
